import { Injectable, inject, signal } from '@angular/core';
import { PlatformIChingConsultationMarkerDto } from '@data/_generated-api/platform-api/models/platform-i-ching-consultation-marker-dto';
import { HdChartGroupService } from '../../chart/library/hd-chart-group.service';
import { HdLifeTimelineMarker } from '../../chart/timeline/hd-life-timeline.models';
import { HdIChingAccessService } from '../hd-iching-access.service';
import { HdIChingJournalService } from '../journal/hd-iching-journal.service';

/** Vaste kleur van I Tjing-momenten op de tijdlijn (het goud van `--hd-iching-accent`). */
export const ICHING_MOMENT_COLOR_HEX = '#d4a869';
/** Icoon van Classic I Ching (zelfde als de poortinfo-knop en de docktab). */
export const ICHING_MOMENT_ICON = 'scroll-old';
/** Momenten uit het dagboek tellen als dagelijkse momenten (categoriefilter). */
export const ICHING_MOMENT_CATEGORY = 'daily';
const LABEL_QUESTION_MAX_LENGTH = 60;

/**
 * Raadplegingen uit het I Tjing-dagboek als markers voor de levenstijdlijn en
 * de kalenders. De raadplegingen van chart X zijn die met `linkedChartId = X`;
 * op de eigen chart (`getMyChart`) komen ook de raadplegingen zonder chart mee
 * (alles wat op de pagina zonder context bewaard is). Zonder
 * `classicVisible()` levert de service niets: de markers bestaan dan niet.
 *
 * `changed` telt op na elke bewaarbeurt of verwijdering via de momentdialoog;
 * hosts herladen hun markers wanneer de teller wijzigt.
 */
@Injectable({ providedIn: 'root' })
export class HdIChingMomentsService {
  private readonly journal = inject(HdIChingJournalService);
  private readonly access = inject(HdIChingAccessService);
  private readonly groupService = inject(HdChartGroupService);

  private readonly changedState = signal(0);
  readonly changed = this.changedState.asReadonly();

  /** Eén keer opgehaald; een mislukte call telt als "geen eigen chart". */
  private myChartId: Promise<string | undefined> | undefined;

  markChanged(): void {
    this.changedState.update(value => value + 1);
  }

  async markersForChart(chartId: string): Promise<HdLifeTimelineMarker[]> {
    if (!this.access.classicVisible()) {
      return [];
    }
    const includeUnlinked = (await this.resolveMyChartId()) === chartId;
    const markers = await this.journal.markersForChart(chartId, includeUnlinked);
    return markers.map(buildIChingMomentMarker).filter((marker): marker is HdLifeTimelineMarker => marker !== undefined);
  }

  private resolveMyChartId(): Promise<string | undefined> {
    this.myChartId ??= this.groupService
      .getMyChart()
      .then(chart => chart?.id ?? undefined)
      .catch(() => undefined);
    return this.myChartId;
  }
}

/** Marker van één raadpleging; zonder id of tijdstip geen marker. */
export function buildIChingMomentMarker(dto: PlatformIChingConsultationMarkerDto): HdLifeTimelineMarker | undefined {
  if (!dto.id || !dto.consultedAt || dto.primaryHexagram == null) {
    return undefined;
  }
  return {
    id: `iching:${dto.id}`,
    kind: 'moment',
    startUtc: dto.consultedAt,
    label: buildIChingMomentLabel(dto.primaryHexagram, dto.changedHexagram ?? undefined, dto.question ?? undefined),
    icon: ICHING_MOMENT_ICON,
    colorHex: ICHING_MOMENT_COLOR_HEX,
    category: ICHING_MOMENT_CATEGORY,
    consultationId: dto.id,
  };
}

/** `#53 ↔ #57 · eerste woorden van de vraag` (zonder omslag enkel `#53`). */
export function buildIChingMomentLabel(primary: number, changed: number | undefined, question: string | undefined): string {
  const hexagrams = changed === undefined ? `#${primary}` : `#${primary} ↔ #${changed}`;
  const trimmed = question?.trim() ?? '';
  if (trimmed.length === 0) {
    return hexagrams;
  }
  const short = trimmed.length > LABEL_QUESTION_MAX_LENGTH ? `${trimmed.slice(0, LABEL_QUESTION_MAX_LENGTH - 1).trimEnd()}…` : trimmed;
  return `${hexagrams} · ${short}`;
}
